import localforage from 'localforage';
import type { Note } from '../types/note';

// Configure localforage
const notesStore = localforage.createInstance({
    name: 'lunar-calendar',
    storeName: 'notes',
});

const syncQueueStore = localforage.createInstance({
    name: 'lunar-calendar',
    storeName: 'sync-queue',
});

// Generate local ID
export function generateLocalId(): string {
    return `local_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

// Notes CRUD operations
export const storage = {
    // Save note to local storage
    async saveNote(note: Note): Promise<void> {
        const key = note.id || note.localId || generateLocalId();
        await notesStore.setItem(key, { ...note, localId: key });
    },

    // Get all notes from local storage
    async getAllNotes(): Promise<Note[]> {
        const notes: Note[] = [];
        await notesStore.iterate<Note, void>((value) => {
            notes.push(value);
        });
        return notes.sort((a, b) =>
            new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
        );
    },

    // Get notes by date
    async getNotesByDate(date: string): Promise<Note[]> {
        const allNotes = await this.getAllNotes();
        return allNotes.filter((note) => note.date === date);
    },

    // Get note by ID
    async getNoteById(id: string): Promise<Note | null> {
        return await notesStore.getItem<Note>(id);
    },

    // Delete note from local storage
    async deleteNote(id: string): Promise<void> {
        await notesStore.removeItem(id);
    },

    // Clear all notes
    async clearAll(): Promise<void> {
        await notesStore.clear();
    },

    // Sync queue operations
    async addToSyncQueue(action: 'create' | 'update' | 'delete', noteId: string): Promise<void> {
        const queue = await this.getSyncQueue();
        queue.push({ action, noteId, timestamp: Date.now() });
        await syncQueueStore.setItem('queue', queue);
    },

    async getSyncQueue(): Promise<Array<{ action: string; noteId: string; timestamp: number }>> {
        return (await syncQueueStore.getItem('queue')) || [];
    },

    async clearSyncQueue(): Promise<void> {
        await syncQueueStore.setItem('queue', []);
    },
};

export default storage;
